import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import React, { Component } from 'react';
import { Col, Row } from "react-bootstrap";
import { Trash } from "react-feather";
import { apiFetch, API } from "../../services/api";

// Base de rendu du widget (iframe embarquée dans les articles de blog).
export const WIDGET_RENDER_BASE = 'https://widget.therunningcollective.com/rotation/';

// Nombre maximum de colonnes (chaussures) du widget, aligné sur le back_api.
export const MAX_WIDGET_ITEMS = 6;

// Nombre d'options affichées dans un sélecteur produit (le catalogue chaussures
// compte plusieurs milliers d'entrées : on affiche une tranche + un filtre).
const MAX_OPTIONS = 200;

// Snippet iframe à coller dans un article. Le titre est échappé pour ne pas
// casser l'attribut title=".." s'il contient un guillemet.
export function buildWidgetSnippet(widget) {
    if (!widget || !widget.id) {
        return "";
    }
    const title = (widget.title || "").replace(/"/g, "&quot;");
    return `<iframe src="${WIDGET_RENDER_BASE}${widget.id}" width="100%" height="720" frameborder="0" loading="lazy" title="${title}"></iframe>`;
}

// Copie presse-papier avec repli sur execCommand (contexte non sécurisé).
export function copyToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
    }
    return new Promise((resolve, reject) => {
        try {
            const textarea = document.createElement("textarea");
            textarea.value = text;
            textarea.style.position = "fixed";
            textarea.style.opacity = "0";
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
            resolve();
        } catch (err) {
            reject(err);
        }
    });
}

const USAGE_OPTIONS = [
    { value: "", label: "—" },
    { value: "training", label: "Entraînement" },
    { value: "competition", label: "Compétition" },
    { value: "mixed", label: "Mixte" },
];

// Note /10 bornée ; "" ou invalide → 0 (= non renseigné côté produit).
export function clampRating(value) {
    const n = parseInt(value, 10);
    if (Number.isNaN(n)) return 0;
    return Math.min(10, Math.max(0, n));
}

// Notes d'un produit telles que stockées sur la fiche produit (0 = non renseigné).
const ratingsOf = (product) => ({
    cushioningRating: clampRating(product && product.cushioningRating),
    reactivityRating: clampRating(product && product.reactivityRating),
    stabilityRating: clampRating(product && product.stabilityRating),
    usageType: (product && product.usageType) || "",
});

const emptyItem = () => ({
    label: "", shortURL: "", reference: "", filter: "",
    product: null, ratings: ratingsOf(null), ratingsLoading: false,
});

// Charge la fiche produit complète (back-products-api) pour afficher/éditer ses notes.
function fetchProduct(shortURL) {
    return apiFetch(API.products + "/product?shortURL=" + encodeURIComponent(shortURL))
        .then(response => response.json());
}

// Les notes vivent sur le PRODUIT (une seule source pour tous les widgets) :
// on renvoie la fiche chargée, complétée des notes saisies, au PUT /product.
function saveProductRatings(product, ratings, language) {
    return apiFetch(API.products + "/product", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...product, ...ratings, language: product.language || language }),
    });
}

const sameRatings = (a, b) =>
    a.cushioningRating === b.cushioningRating && a.reactivityRating === b.reactivityRating &&
    a.stabilityRating === b.stabilityRating && (a.usageType || "") === (b.usageType || "");

// Modale de création / édition d'un widget « rotation de chaussures ».
// Règles reprises du back_api : titre obligatoire, 1 à 6 chaussures, label et
// produit obligatoires par ligne, pas de doublon de produit.
export default class AddShoeRotationWidgetModal extends Component {
    constructor(props) {
        super(props);
        const widget = props.selectedWidget || {};
        const items = (widget.items && widget.items.length > 0 ? widget.items : [{}]).map(item => ({
            ...emptyItem(),
            label: item.label || "",
            shortURL: item.shortURL || "",
            reference: item.reference || "",
        }));
        this.state = {
            id: widget.id || "",
            title: widget.title || "",
            intro: widget.intro || "",
            language: widget.language || "fr",
            items,
            loading: false,
            error: null,
            saved: false,
            copied: false,
        };
    }

    componentDidMount() {
        this.state.items.forEach((item, index) => {
            if (item.shortURL) this.loadRatings(index, item.shortURL);
        });
    }

    updateItem = (index, field, value) => {
        this.setState(prevState => ({
            items: prevState.items.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
        }));
        if (field === "shortURL") {
            this.loadRatings(index, value);
        }
    };

    updateRating = (index, field, value) => {
        const clean = field === "usageType" ? value : clampRating(value);
        this.setState(prevState => ({
            items: prevState.items.map((item, i) =>
                i === index ? { ...item, ratings: { ...item.ratings, [field]: clean } } : item),
        }));
    };

    // Récupère la fiche produit pour pré-remplir les notes actuelles.
    loadRatings = (index, shortURL) => {
        if (!shortURL) {
            this.setState(prevState => ({
                items: prevState.items.map((item, i) =>
                    i === index ? { ...item, product: null, ratings: ratingsOf(null) } : item),
            }));
            return;
        }
        this.setState(prevState => ({
            items: prevState.items.map((item, i) => (i === index ? { ...item, ratingsLoading: true } : item)),
        }));
        fetchProduct(shortURL)
            .then(product => {
                this.setState(prevState => ({
                    items: prevState.items.map((item, i) =>
                        i === index && item.shortURL === shortURL
                            ? { ...item, product, ratings: ratingsOf(product), ratingsLoading: false }
                            : item),
                }));
            })
            .catch(err => {
                console.log(err);
                this.setState(prevState => ({
                    items: prevState.items.map((item, i) => (i === index ? { ...item, ratingsLoading: false } : item)),
                }));
            });
    };

    addItem = () => {
        this.setState(prevState => (
            prevState.items.length >= MAX_WIDGET_ITEMS
                ? null
                : { items: [...prevState.items, emptyItem()] }
        ));
    };

    removeItem = (index) => {
        this.setState(prevState => ({
            items: prevState.items.filter((item, i) => i !== index),
        }));
    };

    // Validation côté client, miroir des règles du back_api.
    validate = () => {
        if (!this.state.title.trim()) {
            return "Le titre est obligatoire";
        }
        const items = this.state.items;
        if (items.length < 1 || items.length > MAX_WIDGET_ITEMS) {
            return `Le widget doit contenir entre 1 et ${MAX_WIDGET_ITEMS} chaussures`;
        }
        const seen = {};
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (!item.shortURL) {
                return `Ligne ${i + 1} : sélectionnez une chaussure`;
            }
            if (!item.label.trim()) {
                return `Ligne ${i + 1} : le libellé de colonne est obligatoire`;
            }
            if (seen[item.shortURL]) {
                return `Ligne ${i + 1} : cette chaussure est déjà présente dans le widget`;
            }
            seen[item.shortURL] = true;
        }
        return null;
    };

    handleSubmit = () => {
        const error = this.validate();
        if (error) {
            this.setState({ error });
            return;
        }
        this.setState({ loading: true, error: null });

        const body = {
            id: this.state.id,
            title: this.state.title.trim(),
            intro: this.state.intro,
            language: this.state.language,
            items: this.state.items.map(item => ({
                label: item.label.trim(),
                shortURL: item.shortURL,
                reference: (item.reference || "").trim(),
            })),
        };

        // Notes modifiées → enregistrées sur chaque produit concerné avant le widget.
        const ratingUpdates = this.state.items
            .filter(item => item.product && !sameRatings(ratingsOf(item.product), item.ratings))
            .map(item => saveProductRatings(item.product, item.ratings, this.state.language)
                .then(() => this.updateItem(this.state.items.indexOf(item), "product", { ...item.product, ...item.ratings })));

        Promise.all(ratingUpdates)
            .then(() => apiFetch(API.back + '/shoe-rotation-widget', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            }))
            .then(response => response.json())
            .then(async result => {
                const id = (result && result.id) ? result.id : this.state.id;
                if (this.props.refresh) {
                    await this.props.refresh();
                }
                this.setState({ id, loading: false, saved: true });
            })
            .catch(err => {
                console.log(err);
                this.setState({ loading: false, error: "Erreur lors de l'enregistrement" });
            });
    };

    handleCopy = () => {
        copyToClipboard(buildWidgetSnippet({ id: this.state.id, title: this.state.title }))
            .then(() => {
                this.setState({ copied: true });
                setTimeout(() => this.setState({ copied: false }), 2500);
            })
            .catch(err => console.log(err));
    };

    // Options du sélecteur produit : filtrées par nom, tranche limitée, et on
    // garde toujours le produit déjà sélectionné pour qu'il reste affiché.
    productOptions = (item) => {
        const products = this.props.availableProducts || [];
        const filter = (item.filter || "").trim().toLowerCase();
        const matching = filter
            ? products.filter(product => (product.name || "").toLowerCase().includes(filter))
            : products;
        const options = matching.slice(0, MAX_OPTIONS);
        if (item.shortURL && !options.some(product => product.shortURL === item.shortURL)) {
            const selected = products.find(product => product.shortURL === item.shortURL);
            options.unshift(selected || { name: item.shortURL, shortURL: item.shortURL });
        }
        return { options, total: matching.length };
    };

    render() {
        const snippet = buildWidgetSnippet({ id: this.state.id, title: this.state.title });

        return (
            <Modal show={this.props.isOpen} onHide={this.props.closeModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{this.props.selectedWidget && this.props.selectedWidget.id ? "Modifier" : "Ajouter"} un widget rotation de chaussures</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Titre : </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Ex : Ma rotation marathon"
                                value={this.state.title}
                                onChange={(e) => this.setState({ title: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Introduction : </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Ex : Trois paires suffisent pour couvrir toutes les séances..."
                                value={this.state.intro}
                                onChange={(e) => this.setState({ intro: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Langue : </Form.Label>
                            <Form.Control
                                as="select"
                                custom
                                value={this.state.language}
                                onChange={(e) => this.setState({ language: e.target.value })}
                            >
                                <option value="fr">Français (.fr)</option>
                                <option value="es">Espagnol (.es)</option>
                            </Form.Control>
                        </Form.Group>

                        <Form.Label>Chaussures du widget ({this.state.items.length}/{MAX_WIDGET_ITEMS}) : </Form.Label>
                        {!this.props.availableProducts ?
                            <p className="text-muted">Chargement du catalogue chaussures ...</p> : null}
                        {this.state.items.map((item, index) => {
                            const { options, total } = this.productOptions(item);
                            return (
                                <div key={index} style={{ border: "1px solid #e0e0e0", borderRadius: 4, padding: 12, marginBottom: 12 }}>
                                    <Row>
                                        <Col sm={11}>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Chaussure : </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Filtrer par nom (ex : pegasus)"
                                                    value={item.filter}
                                                    onChange={(e) => this.updateItem(index, "filter", e.target.value)}
                                                />
                                                <Form.Control
                                                    as="select"
                                                    custom
                                                    value={item.shortURL}
                                                    onChange={(e) => this.updateItem(index, "shortURL", e.target.value)}
                                                >
                                                    <option value="">Sélectionner une chaussure...</option>
                                                    {options.map(product => (
                                                        <option key={product.shortURL} value={product.shortURL}>{product.name}</option>
                                                    ))}
                                                </Form.Control>
                                                {total > options.length ?
                                                    <Form.Text className="text-muted">
                                                        {total} chaussures correspondent, {options.length} affichées : affinez le filtre.
                                                    </Form.Text> : null}
                                            </Form.Group>
                                        </Col>
                                        <Col sm={1}>
                                            <Button variant="secondary" style={{ marginTop: 30 }}
                                                    onClick={() => this.removeItem(index)}
                                                    disabled={this.state.items.length <= 1}>
                                                <Trash size={10}/>
                                            </Button>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col sm={6}>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Libellé de la colonne : </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Polyvalent"
                                                    value={item.label}
                                                    onChange={(e) => this.updateItem(index, "label", e.target.value)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col sm={6}>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Référence coloris (optionnel) : </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="1011B974-001"
                                                    value={item.reference}
                                                    onChange={(e) => this.updateItem(index, "reference", e.target.value)}
                                                />
                                                <Form.Text className="text-muted">Force un coloris précis du produit.</Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        {[
                                            ["cushioningRating", "Amorti /10"],
                                            ["reactivityRating", "Réactivité /10"],
                                            ["stabilityRating", "Stabilité /10"],
                                        ].map(([field, label]) => (
                                            <Col sm={3} key={field}>
                                                <Form.Group className="mb-2">
                                                    <Form.Label>{label} : </Form.Label>
                                                    <Form.Control
                                                        type="number" min={0} max={10} step={1}
                                                        disabled={!item.shortURL || item.ratingsLoading}
                                                        value={item.ratings[field]}
                                                        onChange={(e) => this.updateRating(index, field, e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        ))}
                                        <Col sm={3}>
                                            <Form.Group className="mb-2">
                                                <Form.Label>Utilisation : </Form.Label>
                                                <Form.Control
                                                    as="select" custom
                                                    disabled={!item.shortURL || item.ratingsLoading}
                                                    value={item.ratings.usageType}
                                                    onChange={(e) => this.updateRating(index, "usageType", e.target.value)}
                                                >
                                                    {USAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                </Form.Control>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Form.Text className="text-muted">
                                        {item.ratingsLoading ? "Chargement des notes du produit..." :
                                            "Les notes sont enregistrées sur la fiche produit (0 = non renseigné) et partagées par tous les widgets."}
                                    </Form.Text>
                                </div>
                            );
                        })}
                        <Button variant="secondary" onClick={this.addItem}
                                disabled={this.state.items.length >= MAX_WIDGET_ITEMS}>
                            + Ajouter une chaussure
                        </Button>

                        {snippet ?
                            <Form.Group className="mb-3" style={{ marginTop: 20 }}>
                                <Form.Label>Snippet à intégrer dans l'article : </Form.Label>
                                <Form.Control as="textarea" rows={3} readOnly value={snippet}/>
                                <Button variant="secondary" style={{ marginTop: 8 }} onClick={this.handleCopy}>
                                    {this.state.copied ? "Copié !" : "Copier le snippet"}
                                </Button>
                            </Form.Group> : null}

                        {this.state.saved ?
                            <p style={{ color: "#27ae60", marginTop: 12 }}>Widget enregistré.</p> : null}
                        {this.state.error ? <p style={{ color: "#c0392b" }}>{this.state.error}</p> : null}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.props.closeModal}>
                        {this.state.saved ? "Fermer" : "Annuler"}
                    </Button>
                    <Button variant="primary" onClick={this.handleSubmit} disabled={this.state.loading}>
                        {this.state.loading ? "Enregistrement…" : "Valider"}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}
